import { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Upload.module.css';
import { nanoid } from '@reduxjs/toolkit';

import Uppy from '@uppy/core';
import Tus from '@uppy/tus';

import SimpleBar from 'simplebar-react';
import useSimpleBar from '../../hooks/useSimpleBar';

import { initPDF, pdfManager } from '../../utils/pdfium/pdfManager';
import { PDFium } from '../../utils/pdfium/pdfium-manager';

import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { useInterval } from '../../hooks/useInterval';
import { useSlice } from '../../hooks/useSlice';
import { usePage } from '../../hooks/usePage';
import useModal from '../../hooks/useModal';

import { mainApiSlice } from '../../services/main';
import { settingsApiSlice } from '../../services/settings';
import { apiSlice } from '../../services/api';
import {
  libApiSlice,
  useDeleteLibResourcesMutation,
} from '../../services/library';
import { store } from '../../services/store';

import { setIsListChanged } from '../../features/mainSlice';
import { setCredentials } from '../../features/authSlice';
import { selectModalById } from '../../features/modalSlice';

import { Options } from '../../types/library';
import { ResponseBody } from '../../types/common';
import { IModal } from '../../types/modal';

import { getAccessToken, getTokens, setToken } from '../../utils/storage';
import { isOfType } from '../../utils/typeGuard';
import { VITE_API_V3, createBlobFromURL } from '../../utils/util';

import {
  CANCEL_All,
  COMPLETE,
  LOADING,
  OPEN_UPLOAD_LIST,
  PROGRESS,
  RETRY,
  UPLOAD_CANCEL,
} from '../../constants';

import {
  projectIdChanged,
  selectUploadProjectId,
} from '../../features/uploadSlice';

interface Queue {
  id: string;
  name: string;
  percent: number;
  error?: boolean;
}

const endpoint = `${VITE_API_V3}/common/resources/upload/tus`;
const responseMap = new Map();
const successfuls: any[] = [];
let queue: Queue[] = [];
let uploadMsg = '';
let shouldCancel = false;
let isUploadError = false;
let uploadError: Error | null = null;
let isTokenExpired = false;
let pageCnt = 0;
let totalPage = 0;
let projectId: number | null = null;
let isPdf = false;

let fetchProjectId: () => {};

export const uppyLibrary = new Uppy({
  autoProceed: true,
}).use(Tus, {
  removeFingerprintOnSuccess: true,
  async onBeforeRequest(req) {
    req.setHeader('Authorization', `Bearer ${getAccessToken()}`);
  },
  onShouldRetry(err, retryAttempt, options, next) {
    if (
      isOfType(err, 'originalResponse') &&
      err.originalResponse.getStatus() === 401
    ) {
      return true;
    }
    return next(err);
  },
  async onAfterResponse(req, res) {
    if (res.getStatus() === 200) {
      if (res.getBody() !== '') {
        responseMap.set(req.getURL(), JSON.parse(res.getBody()));
      }
    }
  },
  endpoint,
  limit: 1,
});

uppyLibrary
  .on('file-added', ({ id, extension }) => {
    // console.log('file-added', id, extension);
    uppyLibrary.setFileMeta(id, {
      orgExt: extension,
      relativePath: new Date().valueOf(),
    });
  })
  .on('files-added', (files) => {
    console.log('files-added', files, queue);
    uploadMsg = 'uploading';
    const addedQueue = files.map(
      ({ id, name }: { id: string; name: string }) => ({
        id,
        name,
        percent: 0,
      })
    );
    queue = [...queue, ...addedQueue];
  })
  .on('file-removed', (file, reason) => {
    // console.log('file, reason : ', file, reason);
  })
  .on('upload-progress', (file, progress) => {
    // console.log('file, progress : ', file, progress);
    const percent = Math.round(
      (progress.bytesUploaded / progress.bytesTotal) * 100
    );

    if (isOfType(file, 'id')) {
      const current = { id: file?.id, name: file?.name, percent };
      queue = [current, ...queue.filter(({ id }) => id !== file?.id)].sort(
        (a, b) => b.percent - a.percent
      );

      if (shouldCancel) {
        percent !== 100 && uppyLibrary.removeFile(file.id);
        queue = [];
      }
    }
  })
  .on('upload-success', async (file, res) => {
    console.log('upload-success : ', file, res);
    const { resourceId } = responseMap.get(res.uploadURL).data;
    isPdf = file?.name.split('.').pop()?.toLocaleLowerCase() === 'pdf';

    try {
      if (isPdf) {
        fetchProjectId = async () => {
          const { data }: any = await store
            .dispatch(libApiSlice.endpoints.bindPdf.initiate(resourceId))
            .unwrap();
          projectId = data.projectId;
          store.dispatch(projectIdChanged(projectId));
          return projectId;
        };
      }
    } catch (error) {
      console.error('Failed to bind resource', error);
    }
  })
  .on('complete', async (result: any) => {
    console.log('complete :::::: ', result, successfuls);
    const responseBodyList: ResponseBody[] = [];
    let convertedPdfResourceId = 0;

    if (successfuls.length) {
      successfuls.push(...result.successful);
      return;
    }

    if (result.failed.length) {
      result.failed.forEach((failed: any) => {
        uppyLibrary.retryUpload(failed.id);
      });
    }

    if (!isPdf) {
      uploadMsg = 'pending';

      const file = result.successful[0];
      const { resourceId } = responseMap.get(file.uploadURL).data;
      const { data }: any = await store
        .dispatch(libApiSlice.endpoints.bindConvert.initiate(resourceId))
        .unwrap();

      projectId = data.projectId;
      convertedPdfResourceId = data.resourceId;
      store.dispatch(projectIdChanged(projectId));
    }

    for (const [idx, successful] of result.successful.entries()) {
      responseBodyList.push(responseMap.get(successful.uploadURL));

      if (!idx) {
        const path = isPdf
          ? URL.createObjectURL(successful.data)
          : `${VITE_API_V3}/common/resources/download/resource/${convertedPdfResourceId}`;
        let cnt = 0;

        isPdf && (await fetchProjectId());

        successfuls.push(...result.successful);
        initPDF(path, successful);
        // @ts-ignore
        pdfManager.off('init').on('init', async (pdfRes) => {
          console.log('pdf init ::::: ', pdfRes);
          const { successful, total } = pdfRes;

          const getImageFile = (page: number, dataURI: string) => {
            const blobObj = createBlobFromURL(dataURI);
            const filename = `${successful.name.replace(
              /\.pdf/i,
              ''
            )}_thumb_${page}`;
            const file = new File([blobObj], `${filename}.png`, {
              type: 'image/png',
            });

            return file;
          };

          let thumbnailFile: any = null;
          totalPage = total;

          const uploadPage = async () => {
            pdfManager.getPage(pageCnt).then(async (pageRes) => {
              // console.log('getPage: ', pageRes);
              const { page, pageHeight, pageWidth, search, text, url } =
                pageRes;
              try {
                await store.dispatch(
                  libApiSlice.endpoints.bindPage.initiate({
                    page,
                    pageHeight,
                    pageWidth,
                    searchText: search,
                    text,
                    url,
                    projectId,
                  })
                );
              } catch (error) {
                console.error('Failed to bind the page', error);
              }

              pdfManager
                .getThumbnail(pageCnt)
                .then(async ({ page, data: dataURI }) => {
                  if (!pageCnt) return;

                  if (pageCnt === 1) {
                    thumbnailFile = getImageFile(page, dataURI);
                  }

                  try {
                    await store.dispatch(
                      libApiSlice.endpoints.bindThumbnail.initiate({
                        page,
                        pageHeight: pageRes.pageHeight,
                        pageWidth: pageRes.pageWidth,
                        projectId,
                        url: dataURI,
                      })
                    );
                  } catch (error) {
                    console.error('Failed to bind thumbnail', error);
                  }

                  if (!pageCnt) return;

                  pageCnt++;
                  if (pageCnt <= total) {
                    uploadPage();
                  }

                  if (pageCnt > total) {
                    const coverUppy = new Uppy({
                      autoProceed: true,
                    });

                    coverUppy
                      .use(Tus, {
                        async onBeforeRequest(req) {
                          req.setHeader(
                            'Authorization',
                            `Bearer ${getAccessToken()}`
                          );
                        },
                        onShouldRetry(err, retryAttempt, options, next) {
                          if (
                            isOfType(err, 'originalResponse') &&
                            err.originalResponse.getStatus() === 401
                          ) {
                            return true;
                          }
                          return next(err);
                        },
                        async onAfterResponse(req, res) {
                          if (res.getStatus() === 200) {
                            if (res.getBody() !== '') {
                              responseMap.set(
                                req.getURL(),
                                JSON.parse(res.getBody())
                              );
                            }
                          }
                        },
                        endpoint,
                      })
                      .on('complete', async (res) => {
                        console.log(
                          'Complete Cover Upload',
                          res,
                          successfuls,
                          cnt
                        );
                        !res.failed.length && cnt++;

                        if (successfuls[cnt]) {
                          setTimeout(() => {
                            const path = URL.createObjectURL(
                              successfuls[cnt].data
                            );
                            initPDF(path, successfuls[cnt]);
                          }, 200);
                        } else {
                          uploadMsg = 'completed';
                          queue = [];
                          coverUppy.close();
                        }

                        res.successful.forEach(async (successful) => {
                          const { resourceId } = responseMap.get(
                            successful.uploadURL
                          ).data;
                          coverUppy.removeFile(successful.id);
                          try {
                            await store.dispatch(
                              libApiSlice.endpoints.bindCover.initiate({
                                projectId,
                                resourceId,
                              })
                            );
                            await store
                              .dispatch(
                                libApiSlice.endpoints.finishUpload.initiate(
                                  projectId
                                )
                              )
                              .unwrap();
                          } catch (error) {
                            console.error(
                              'Failed to bind cover or finish upload',
                              error
                            );
                          }
                        });
                      })
                      .on('upload-error', (file, err) => {
                        console.log('Failed to Upload Cover : ', file, err);
                        isUploadError = true;
                        uploadError = err;

                        if (err.message.includes('401_2')) {
                          isTokenExpired = true;
                        }
                      });

                    try {
                      coverUppy.setMeta({ orgCoverYn: 1 });
                      coverUppy.addFile(thumbnailFile);
                    } catch (error) {
                      console.error('Failed to upload the cover', error);
                    }
                  }
                });
            });
          };

          pageCnt = 1;
          uploadPage();
        });
      }
    }
  })
  .on('cancel-all', () => {
    // console.log('cancel-all :::::: ');
  })
  .on('upload-error', (file, err, res) => {
    console.log('upload-error : ', file, err);
    isUploadError = true;
    uploadError = err;

    if (err.message.includes('401_2')) {
      isTokenExpired = true;
    } else {
      queue = queue.map((el) =>
        el.id === file?.id ? { ...el, error: true } : el
      );
    }
  });

export default function UploadLibrary({ isHidden }: IModal) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();

  const [simplebarRef] = useSimpleBar();
  const { selectOptions } = useSlice();
  const { sortType, sortOrder }: Options = useAppSelector(selectOptions);
  const convertingModal = useAppSelector((state) =>
    selectModalById(state, 'pdfConverting')
  );
  const { pageChanged, initPageList } = usePage();
  const { toggleModal, updateModalData, closeAllModals } = useModal();
  const selectedProjectId = useAppSelector(selectUploadProjectId);

  const [isOpenList, setIsOpenList] = useState(false);
  const [list, setList] = useState(queue);
  const [isError, setIsError] = useState(isUploadError);
  const [currentPage, setCurrentPage] = useState(0);

  const [getStorage] = mainApiSlice.useLazyGetStorageQuery();
  const [getSettingsStorage] =
    settingsApiSlice.useLazyGetSettingsStorageQuery();
  const [deleteLibResources] = useDeleteLibResourcesMutation();

  const listLen = list.length;

  const toggleList = () => setIsOpenList(!isOpenList);

  const initPage = () => {
    if (pageChanged) {
      dispatch(pageChanged(0));
      dispatch(
        initPageList({
          page: 0,
          sortBy: sortType,
          sortDir: sortOrder,
          id: nanoid(),
        })
      );
    }
    try {
      getStorage();
      getSettingsStorage();
      dispatch(setIsListChanged(true));
    } catch (error) {
      console.error('Failed to initialize', console.error);
    }
  };

  const cancelUpload = (id?: string) => {
    if (id) {
      uppyLibrary.removeFile(id);
      queue = queue.filter(({ id: qId }) => qId !== id);
    } else {
      queue = [];
    }
    selectedProjectId && deleteProject(selectedProjectId);
    shouldCancel = true;
  };

  const retryUpload = (id: string) => {
    uppyLibrary.retryUpload(id);
  };

  const deleteProject = async (id: number) => {
    try {
      await deleteLibResources(id);
    } catch (error) {
      console.error('Failed to delete project', error);
    }
  };

  const hideUploadProgress = () => {
    updateModalData({
      name: 'upload-library',
      id: 'upload-library-progress',
      type: 'upload',
      isHidden: true,
    });
  };

  const openConvertingModal = (data: {}) => {
    toggleModal({
      name: 'pdf-converting',
      id: 'pdfConverting',
      type: 'confirm',
      isOpen: true,
      data,
    });
  };

  useInterval(
    () => {
      if (
        !queue.length &&
        (!uploadMsg || uploadMsg === 'completed' || shouldCancel)
      ) {
        pageCnt = 0;
        totalPage = 0;
        uploadMsg = '';
        shouldCancel = false;
        successfuls.length = 0;
        setList(queue);
        setCurrentPage(0);
        dispatch(projectIdChanged(0));
        uppyLibrary.cancelAll();
        PDFium.close();

        setTimeout(() => {
          closeAllModals();
          initPage();
        }, 800);
      } else if (isUploadError) {
        setIsError(true);
      } else if (uploadMsg === 'pending') {
        uploadMsg = 'converting';
        openConvertingModal({ status: 'pending' });
      } else {
        setList(queue);
        pageCnt && setCurrentPage(pageCnt);
      }
    },
    !queue.length && (!uploadMsg || uploadMsg === 'completed') ? null : 40
  );

  useEffect(() => {
    if (isError) {
      if (isTokenExpired) {
        dispatch(apiSlice.endpoints.refreshToken.initiate(getTokens()))
          .unwrap()
          .then((res: any) => {
            const tokens = res.data;
            isUploadError = false;
            uploadError = null;
            isTokenExpired = false;
            dispatch(setCredentials(tokens));
            setToken(tokens);
            setIsError(false);
          });
      }
    }
  }, [isError]);

  useEffect(() => {
    if (currentPage > 0 && currentPage <= totalPage) {
      uploadMsg = 'converting';
      openConvertingModal({ current: currentPage, total: totalPage });
    }
  }, [currentPage]);

  useEffect(() => {
    if (convertingModal?.isCancel) {
      shouldCancel = true;
      queue = [];
      projectId && deleteProject(projectId);
    }
  }, [convertingModal]);

  useEffect(() => {
    if (!uploadMsg) {
      if (selectedProjectId) {
        deleteProject(selectedProjectId);
        dispatch(projectIdChanged(0));

        setTimeout(() => {
          initPage();
        }, 600);
      }
      closeAllModals();
    }
  }, []);

  return (
    <div className={cx('upload--container', 'library', { hide: isHidden })}>
      <div className={cx('upload_layer', { max: isOpenList })}>
        <div className={cx('title')}>
          <h2>
            {PROGRESS}(<span>{listLen}</span>)
          </h2>
          <span
            role="button"
            className={cx('btn', 'upload_cancel')}
            title={CANCEL_All}
            onClick={() => cancelUpload()}
          >
            <span>{CANCEL_All}</span>
          </span>
          {listLen > 2 && (
            <span
              role="button"
              className={cx('btn', 'upload_items_open')}
              aria-label={OPEN_UPLOAD_LIST}
              onClick={toggleList}
            >
              <i className={cx('upload_arrow', { open: isOpenList })}></i>
              <span className={cx('blind')}>{OPEN_UPLOAD_LIST}</span>
            </span>
          )}
        </div>

        <SimpleBar forceVisible={true} ref={simplebarRef}>
          <div className={cx('inner')}>
            <div className={cx('scroll_box')}>
              {list.map(({ id, name, percent, error }: any) => {
                const isComplete = percent === 100;

                return (
                  <div
                    key={id}
                    className={cx(
                      'upload_items',
                      { 'is--done': isComplete } /* 'is--retry' */
                    )}
                  >
                    <div className={cx('upload_filename')}>{name}</div>

                    <div className={cx('upload_status')}>
                      {!isComplete && (
                        <div
                          className={cx('btn', 'upload_cancel')}
                          title={UPLOAD_CANCEL}
                          onClick={() => cancelUpload(id)}
                        >
                          <span className={cx('blind')}>{UPLOAD_CANCEL}</span>
                        </div>
                      )}

                      <div className={cx('progress_bar')}>
                        <div
                          className={cx('progress', 'rail')}
                          style={{ width: '100%' }}
                        ></div>
                        <div
                          className={cx('progress', 'current')}
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>

                      <div className={cx('ctrl_status')}>
                        {!error ? (
                          <span className={cx('txt')}>
                            {!percent
                              ? LOADING
                              : isComplete
                              ? COMPLETE
                              : `${percent}%`}
                          </span>
                        ) : (
                          <span
                            role="button"
                            className={cx('btn', 'upload_retry')}
                            aria-label={RETRY}
                            onClick={() => retryUpload(id)}
                          >
                            <span>{RETRY}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SimpleBar>
      </div>
    </div>
  );
}
